import Axios, { AxiosResponse } from "axios";
import { PUSH_NOTIFICATION_PUBLIC_KEY } from "../shared/utilty/constants";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";
import { KMainFunctions } from "../shared/utilty/main-functions";
import { UserService } from "./user-service";

const backendUrl = KuikaAppManager.GetBackendUrl();

interface ISendPushNotificationToUsernamePayload {
  username: string;
  title: string;
  message: string;
  extraData?: { [key: string | number]: unknown };
}

class NotificationService {
  public static async SendPushNotificationToUsername(
    payload: ISendPushNotificationToUsernamePayload
  ): Promise<AxiosResponse> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/notification/sendtouser`, {
        ...payload,
        extraData: payload.extraData ? JSON.stringify(payload.extraData) : undefined
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}

export async function registerServiceWorker() {
  // Service Workers only work with http:, https:, or localhost
  // Skip registration for custom protocols (e.g., kuika:// in Electron)
  const protocol = window.location.protocol;
  if (protocol !== "http:" && protocol !== "https:") {
    console.info("Service Worker registration skipped: unsupported protocol", protocol);
    return;
  }

  if (!("serviceWorker" in navigator)) {
    console.warn("Service Worker is not supported.");
    return;
  }

  // In dev server mode no sw.js is emitted (InjectManifest is disabled).
  // Unregister any stale SW from a previous session to prevent the SW from
  // serving cached assets while HMR is active, which would cause an infinite
  // page-reload loop (SW activates → controllerchange → reload → new SW → …).
  if (process.env.DEV_SERVER === "true") {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    } catch {
      // best-effort
    }
    return;
  }

  try {
    const registration = await navigator.serviceWorker.register(`${KuikaAppManager.GetFrontEndUrl()}/sw.js`);
    return registration;
  } catch (error) {
    console.error("Service Worker registration failed", error);
  }
}

function waitForFirstUserGesture(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));

    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    const handler = () => {
      cleanup();
      resolve();
    };

    const cleanup = () => {
      window.removeEventListener("pointerdown", handler, true);
      window.removeEventListener("keydown", handler, true);
      window.removeEventListener("touchstart", handler, true);
      signal?.removeEventListener("abort", onAbort);
    };

    window.addEventListener("pointerdown", handler, true);
    window.addEventListener("keydown", handler, true);
    window.addEventListener("touchstart", handler, true);
    signal?.addEventListener("abort", onAbort);
  });
}

let inFlight: Promise<NotificationPermission> | null = null;

export async function askPermission(options?: { signal?: AbortSignal }) {
  if (PUSH_NOTIFICATION_PUBLIC_KEY === "") {
    return undefined as any;
  }

  if (!("Notification" in window)) {
    throw new Error("Notifications bu tarayıcıda desteklenmiyor");
  }

  if (inFlight) return inFlight;

  inFlight = (async () => {
    const current = Notification.permission;

    if (current === "granted") return current;

    if (current === "denied") {
      throw new Error("Push Notification izni kapalı (site settings'ten açılmalı)");
    }

    await waitForFirstUserGesture(options?.signal);

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      throw new Error("Push Notification izni verilmedi");
    }
    return permission;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export async function subscribeUser(username: string) {
  if (PUSH_NOTIFICATION_PUBLIC_KEY !== "") {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(PUSH_NOTIFICATION_PUBLIC_KEY)
      });
    }

    const payload = {
      deviceId: subscription.endpoint,
      registrationId: arrayBufferToBase64Url(subscription.getKey("p256dh")),
      appBundleId: arrayBufferToBase64Url(subscription.getKey("auth")),
      userName: username,
      deviceType: 0,
      publicKey: PUSH_NOTIFICATION_PUBLIC_KEY
    };

    UserService.RegisterDevice(payload);
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export default NotificationService;
